package org.teddapp.project.ui.finance

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import com.tedd.db.Finance_transaction

/**
 * Insère un espace tous les 3 chiffres en partant de la droite — uniquement
 * sur la partie entière. Les séparateurs décimaux courants ('.', ',') sont
 * conservés et la partie fractionnaire est laissée telle quelle.
 *
 * L'état stocké côté caller reste la saisie brute (ex: "1234567" ou "12345.67").
 * Le rendu visuel passe par cette transformation, avec mapping de curseur
 * correct entre raw et formatted.
 */
internal class ThousandsVisualTransformation(
    private val decimalSeparators: Set<Char> = setOf('.', ','),
) : VisualTransformation {

    override fun filter(text: AnnotatedString): TransformedText {
        val raw = text.text
        if (raw.isEmpty()) return TransformedText(AnnotatedString(""), OffsetMapping.Identity)

        val sepIdx = raw.indexOfFirst { it in decimalSeparators }
        val intPart = if (sepIdx == -1) raw else raw.substring(0, sepIdx)
        val tail = if (sepIdx == -1) "" else raw.substring(sepIdx)

        val formattedInt = if (intPart.length <= 3) intPart
        else intPart.reversed().chunked(3).joinToString(" ").reversed()
        val formatted = formattedInt + tail

        val mapping = object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int {
                val n = offset.coerceIn(0, raw.length)
                return if (n <= intPart.length) {
                    var spaces = 0
                    val upper = minOf(n, intPart.length - 1)
                    for (i in 1..upper) {
                        if ((intPart.length - i) % 3 == 0) spaces++
                    }
                    n + spaces
                } else {
                    val totalIntSpaces = if (intPart.length > 1) (intPart.length - 1) / 3 else 0
                    n + totalIntSpaces
                }
            }

            override fun transformedToOriginal(offset: Int): Int {
                val n = offset.coerceIn(0, formatted.length)
                var orig = 0
                for (i in 0 until n) {
                    if (formatted[i] != ' ') orig++
                }
                return orig
            }
        }
        return TransformedText(AnnotatedString(formatted), mapping)
    }
}

/**
 * Variante masquée : remplace le chiffre par "•••" si `hidden` est vrai.
 * Le symbole de devise reste visible pour conserver le repère.
 */
fun fmtMoneyMasked(n: Long?, currency: String = "XOF", hidden: Boolean, compact: Boolean = false): String {
    if (!hidden) return fmtMoney(n, currency, compact)
    val sym = when (currency) {
        "USD" -> "$"
        "EUR" -> "€"
        else -> "F"
    }
    return if (currency == "USD") "$sym •••" else "••• $sym"
}

/**
 * Format monétaire à la fr-FR : "1 234 567 F" / "$ 412" / compact "2,3 M".
 */
fun fmtMoney(n: Long?, currency: String = "XOF", compact: Boolean = false): String {
    if (n == null) return "—"
    val abs = if (n < 0) -n else n
    val str = when {
        compact && abs >= 1_000_000L -> {
            val divided = abs.toDouble() / 1_000_000.0
            val rounded = if (abs >= 10_000_000L) divided.toLong().toString()
                          else ((divided * 10.0).toLong() / 10.0).toString().replace(".", ",")
            "$rounded M"
        }
        compact && abs >= 10_000L -> "${(abs / 1000L)} k"
        else -> abs.toString().reversed().chunked(3).joinToString(" ").reversed()
    }
    val sym = when (currency) {
        "USD" -> "$"
        "EUR" -> "€"
        else -> "F"
    }
    val sign = if (n < 0) "−" else ""
    return if (currency == "USD") "$sign$sym $str" else "$sign$str $sym"
}

/**
 * Montant signé selon le type. expense → négatif, income/transfer → positif.
 */
fun txSignedAmount(tx: Finance_transaction): Long = when (tx.type) {
    "income" -> tx.amount
    "expense" -> -tx.amount
    else -> tx.amount
}

/**
 * Date "YYYY-MM-DD" → "DD/MM" simple, sans dépendance kotlinx-datetime.
 */
fun fmtDateShort(iso: String): String {
    if (iso.length < 10) return iso
    val y = iso.substring(0, 4)
    val m = iso.substring(5, 7)
    val d = iso.substring(8, 10)
    return "$d/$m/$y"
}

/**
 * Étiquette "AUJOURD'HUI", "HIER" ou "IL Y A N JOURS" en fonction du delta de jours
 * entre la date ISO et la date d'aujourd'hui ISO (pas de fuseau, juste lexicographie + arith).
 */
fun dayLabelFromIso(iso: String, todayIso: String): String {
    val a = isoToDayNumber(iso) ?: return iso
    val b = isoToDayNumber(todayIso) ?: return iso
    val delta = b - a
    return when {
        delta <= 0L -> "AUJOURD'HUI"
        delta == 1L -> "HIER"
        delta in 2L..6L -> "IL Y A $delta JOURS"
        else -> {
            // Plus que 6 jours : on retombe sur le format date.
            fmtDateShort(iso).uppercase()
        }
    }
}

/**
 * Convertit "YYYY-MM-DD" en jour julien proleptique grossier (jours depuis l'an 0 — uniquement
 * pour différences relatives, pas pour conversion absolue).
 */
fun isoToDayNumber(iso: String): Long? {
    if (iso.length < 10) return null
    val y = iso.substring(0, 4).toIntOrNull() ?: return null
    val m = iso.substring(5, 7).toIntOrNull() ?: return null
    val d = iso.substring(8, 10).toIntOrNull() ?: return null
    // Algorithme de Howard Hinnant (days_from_civil), portable.
    val ya = if (m <= 2) y - 1 else y
    val era = (if (ya >= 0) ya else ya - 399) / 400
    val yoe = (ya - era * 400).toLong()
    val mp = if (m > 2) m - 3 else m + 9
    val doy = (153 * mp + 2) / 5 + d - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    return era.toLong() * 146097L + doe - 719468L
}
